
import { EventEmitter } from "eventemitter3";
import { registerAudioWorklets } from "./audioworklet-registry";
import { arrayBufferToBase64 } from "./utils";

export class AudioRecorder extends EventEmitter {
  public stream: MediaStream | null = null;
  private audioContext: AudioContext | null = null;
  private source: MediaStreamAudioSourceNode | null = null;
  private processor: AudioWorkletNode | null = null;
  private sampleRate: number;
  private silenceTimer: NodeJS.Timeout | null = null;
  private _isSpeaking: boolean = false;
  private silenceThreshold: number = 0.01; // Audio level threshold for silence
  private silenceDuration: number = 500; // ms of silence before emitting "stopped"
  private minSpeechDuration: number = 500; // ms
  private speechStartTime: number = 0;
  /** Timestamp (epoch ms) of the last speech activity — used by capture gating */
  private _lastSpeechAt: number = 0;

  /** Whether the user is currently speaking (VAD-based) */
  get speaking(): boolean { return this._isSpeaking; }

  /**
   * Whether the user spoke recently (within `windowMs`).
   * Used by visual capture to gate image sending:
   * only capture when user is speaking or just finished speaking.
   */
  spokeRecently(windowMs: number = 3000): boolean {
    if (this._isSpeaking) return true;
    return (Date.now() - this._lastSpeechAt) < windowMs;
  }

  // Two independent reasons the mic is silenced: the user paused the session
  // (_isPaused) or is typing in the chat input (_isMuted). Mic tracks are only
  // live when neither applies, so one can't re-enable the mic under the other.
  private _isMuted: boolean = false;
  private _isPaused: boolean = false;

  get isMuted(): boolean {
    return this._isMuted;
  }

  public get isPaused() {
    return this._isPaused;
  }

  private syncTrackState() {
    const enabled = !this._isMuted && !this._isPaused;
    this.stream?.getAudioTracks().forEach((track) => {
      track.enabled = enabled;
    });
  }

  private resetSpeechDetection() {
    if (this.silenceTimer) {
      clearTimeout(this.silenceTimer);
      this.silenceTimer = null;
    }
    this._isSpeaking = false;
  }

  mute() {
    this._isMuted = true;
    this.syncTrackState();
    this.resetSpeechDetection();
  }

  unmute() {
    this._isMuted = false;
    this.syncTrackState();
  }

  constructor(sampleRate: number = 16000) {
    super();
    this.sampleRate = sampleRate;
  }

  /**
   * Pause audio capture by muting all microphone tracks and disconnecting
   * the processor. The MediaStream and AudioContext stay alive so we can
   * resume without re-requesting mic permissions or recreating the pipeline.
   */
  pause() {
    if (this._isPaused) return;
    this._isPaused = true;

    // Mute all mic tracks so no audio data flows
    this.syncTrackState();

    // Disconnect processor from source to stop the worklet processing
    if (this.source && this.processor) {
      try {
        this.source.disconnect(this.processor);
      } catch {
        // Already disconnected — safe to ignore
      }
    }

    this.resetSpeechDetection();
  }

  /**
   * Resume audio capture after a pause. Re-enables mic tracks and
   * reconnects the processor node to the source.
   */
  unpause() {
    if (!this._isPaused) return;
    this._isPaused = false;

    // Re-enable mic tracks (stay silent if the chat input still has the mic muted)
    this.syncTrackState();

    // Reconnect processor to source
    if (this.source && this.processor) {
      try {
        this.source.connect(this.processor);
      } catch {
        // Already connected — safe to ignore
      }
    }
  }

  async start() {
    try {
      this.stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          channelCount: 1,
          sampleRate: this.sampleRate,
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
      });

      this.audioContext = new AudioContext();
      await registerAudioWorklets(this.audioContext);

      this.source = this.audioContext.createMediaStreamSource(this.stream);
      try {
        if (typeof window !== 'undefined' && (window as any).tourRecorder) {
          (window as any).tourRecorder.registerAudioSource(this.source);
        }
      } catch {}
      this.processor = new AudioWorkletNode(this.audioContext, "audio-recorder-processor", {
        processorOptions: {
          inputSampleRate: this.audioContext.sampleRate,
          outputSampleRate: this.sampleRate,
        }
      });

      this.processor.port.onmessage = (event) => {
        if (this._isMuted || this._isPaused) {
          return;
        }
        const int16Data = event.data;
        const base64Audio = arrayBufferToBase64(int16Data.buffer);
        this.emit("data", base64Audio);

        // Detect silence for thinking state
        const audioLevel = this.calculateAudioLevel(int16Data);

        if (audioLevel > this.silenceThreshold) {
          // User is speaking
          if (!this._isSpeaking) {
            this._isSpeaking = true;
            this.speechStartTime = Date.now();
          }
          this._lastSpeechAt = Date.now();
          // Reset silence timer
          if (this.silenceTimer) {
            clearTimeout(this.silenceTimer);
            this.silenceTimer = null;
          }
        } else {
          // Silence detected
          if (this._isSpeaking && !this.silenceTimer) {
            // Start silence timer
            this.silenceTimer = setTimeout(() => {
              this._isSpeaking = false;
              this.silenceTimer = null;

              const speechDuration = Date.now() - this.silenceDuration - this.speechStartTime;
              if (speechDuration > this.minSpeechDuration) {
                this.emit("stopped");
              } else {
                console.log("Ignored short noise (" + speechDuration + "ms)");
              }
            }, this.silenceDuration);
          }
        }
      };

      this.source.connect(this.processor);
      // Connect processor to a silent sink (gain=0) so the audio graph keeps
      // process() running without routing mic input to speakers — prevents
      // feedback loop on all devices (PC speakers, mobile, Bluetooth)
      const silentSink = this.audioContext.createGain();
      silentSink.gain.value = 0;
      this.processor.connect(silentSink);
      silentSink.connect(this.audioContext.destination);
    } catch (error) {
      console.error("Error starting audio recorder:", error);
      throw error;
    }
  }

  private calculateAudioLevel(int16Data: Int16Array): number {
    let sum = 0;
    for (let i = 0; i < int16Data.length; i++) {
      sum += Math.abs(int16Data[i]) / 32768; // Normalize to 0-1
    }
    return sum / int16Data.length;
  }

  stop() {
    this._isMuted = false;
    if (this.silenceTimer) {
      clearTimeout(this.silenceTimer);
      this.silenceTimer = null;
    }
    if (this.processor) {
      this.processor.disconnect();
      this.processor = null;
    }
    if (this.source) {
      try {
        if (typeof window !== 'undefined' && (window as any).tourRecorder) {
          (window as any).tourRecorder.unregisterAudioSource(this.source);
        }
      } catch {}
      this.source.disconnect();
      this.source = null;
    }
    if (this.audioContext) {
      this.audioContext.close();
      this.audioContext = null;
    }
    if (this.stream) {
      this.stream.getTracks().forEach((track) => track.stop());
      this.stream = null;
    }
    this._isSpeaking = false;
    this._isPaused = false;
  }
}
